import React, { useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useAuth } from '../hooks/use-auth';
import { useSubscription } from '../contexts/SubscriptionContext';
import { Navbar } from '../components/navbar';
import { Sidebar } from '../components/sidebar';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { useToast } from '../hooks/use-toast';
import { apiRequest, getQueryFn, queryClient } from '../lib/queryClient';
import { Link } from 'wouter';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';

// Subscription Management Panel
function SubscriptionManagementPanel() {
  const { userSubscription, companySubscriptions, refreshSubscriptions } = useSubscription();
  const { toast } = useToast();
  const [isCanceling, setIsCanceling] = useState(false);

  const cancelSubscriptionMutation = useMutation({
    mutationFn: async ({ subscriptionId, subscriptionType }: { subscriptionId: number; subscriptionType: string }) => {
      const res = await apiRequest('POST', '/api/subscription/cancel', {
        subscription_id: subscriptionId,
        subscription_type: subscriptionType,
      });
      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }
      return await res.json();
    },
    onSuccess: () => {
      toast({
        title: 'Subscription canceled',
        description: 'Your subscription will be canceled at the end of the current period.',
      });
      refreshSubscriptions();
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to cancel subscription',
        variant: 'destructive',
      });
    },
  });

  const handleCancelSubscription = async () => {
    if (!userSubscription?.subscription_id || !userSubscription?.subscription_type) return;
    
    setIsCanceling(true);
    try {
      await cancelSubscriptionMutation.mutateAsync({
        subscriptionId: userSubscription.subscription_id,
        subscriptionType: userSubscription.subscription_type,
      });
    } finally {
      setIsCanceling(false);
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  const formatPrice = (cents: number) => {
    return `$${(cents / 100).toFixed(2)}`;
  };

  return (
    <div className="space-y-6">
      {/* Current Subscription Status */}
      <Card>
        <CardHeader>
          <CardTitle>Current Subscription</CardTitle>
        </CardHeader>
        <CardContent>
          {userSubscription?.has_active ? (
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-green-50 border border-green-200 rounded-lg">
                <div>
                  <h3 className="font-semibold text-green-800">{userSubscription.plan_name}</h3>
                  <p className="text-sm text-green-600">
                    Status: <span className="capitalize">{userSubscription.status}</span>
                    {userSubscription.current_period_end && (
                      <span> • Renews {formatDate(userSubscription.current_period_end)}</span>
                    )}
                  </p>
                </div>
                <div className="text-right">
                  <div className="text-2xl font-bold text-green-800">
                    {userSubscription.plan_name?.includes('Monthly') ? '$29' : 
                     userSubscription.plan_name?.includes('Yearly') ? '$278.40' : 
                     userSubscription.plan_name?.includes('Lifetime') ? '$999' : 'N/A'}
                  </div>
                  <div className="text-sm text-green-600">
                    {userSubscription.plan_name?.includes('Monthly') ? '/month' : 
                     userSubscription.plan_name?.includes('Yearly') ? '/year' : 
                     userSubscription.plan_name?.includes('Lifetime') ? 'one-time' : ''}
                  </div>
                </div>
              </div>

              {/* Features */}
              <div>
                <h4 className="font-medium mb-3">Included Features</h4>
                <div className="grid grid-cols-2 gap-2">
                  {Object.entries(userSubscription.features).map(([feature, enabled]) => (
                    <div key={feature} className="flex items-center space-x-2">
                      <span className={enabled ? 'text-green-500' : 'text-gray-400'}>
                        {enabled ? '✓' : '✗'}
                      </span>
                      <span className={enabled ? 'text-gray-900' : 'text-gray-500'}>
                        {feature.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Actions */}
              <div className="flex space-x-4 pt-4 border-t">
                <Link href="/pricing">
                  <Button variant="outline">
                    <i data-feather="arrow-up" className="w-4 h-4 mr-2"></i>
                    Upgrade Plan
                  </Button>
                </Link>
                
                {userSubscription.plan_name?.includes('Lifetime') ? (
                  <Button disabled variant="outline">
                    <i data-feather="x-circle" className="w-4 h-4 mr-2"></i>
                    Cannot Cancel Lifetime
                  </Button>
                ) : (
                  <Button
                    variant="outline"
                    onClick={handleCancelSubscription}
                    disabled={isCanceling}
                    className="text-red-600 border-red-300 hover:bg-red-50"
                  >
                    <i data-feather="x-circle" className="w-4 h-4 mr-2"></i>
                    {isCanceling ? 'Canceling...' : 'Cancel Subscription'}
                  </Button>
                )}
              </div>
            </div>
          ) : (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i data-feather="credit-card" className="w-8 h-8 text-gray-400"></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No Active Subscription</h3>
              <p className="text-gray-600 mb-6">
                You're currently using the free tier with limited features.
              </p>
              <Link href="/pricing">
                <Button className="bg-green-600 hover:bg-green-700">
                  <i data-feather="arrow-up" className="w-4 h-4 mr-2"></i>
                  Choose a Plan
                </Button>
              </Link>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Company Subscriptions */}
      {Object.keys(companySubscriptions).length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Company Subscriptions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {Object.entries(companySubscriptions).map(([companyId, subscription]) => (
                <div key={companyId} className="p-4 border border-gray-200 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-medium">{subscription.plan_name}</h4>
                      <p className="text-sm text-gray-600">
                        Company ID: {companyId} • Status: {subscription.status}
                      </p>
                    </div>
                    <div className="text-right">
                      <div className="text-lg font-semibold">
                        {subscription.plan_name?.includes('Monthly') ? '$29' : 
                         subscription.plan_name?.includes('Yearly') ? '$278.40' : 
                         subscription.plan_name?.includes('Lifetime') ? '$999' : 'N/A'}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Billing Information */}
      <Card>
        <CardHeader>
          <CardTitle>Billing Information</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="bg-blue-50 p-4 rounded-lg">
              <div className="flex items-center">
                <i data-feather="info" className="w-5 h-5 text-blue-600 mr-2"></i>
                <div>
                  <h4 className="font-medium text-blue-900">Payment Management</h4>
                  <p className="text-sm text-blue-800 mt-1">
                    Manage your payment methods, view invoices, and update billing information through Stripe's secure customer portal.
                  </p>
                </div>
              </div>
            </div>
            
            <div className="flex space-x-4">
              <Button variant="outline" className="flex-1">
                <i data-feather="external-link" className="w-4 h-4 mr-2"></i>
                Manage Billing
              </Button>
              <Button variant="outline" className="flex-1">
                <i data-feather="download" className="w-4 h-4 mr-2"></i>
                Download Invoices
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// Lightweight panel that mirrors the Companies list for quick registration inside Settings
function CompanyRegistrationPanel() {
  const { user } = useAuth();
  interface Company { 
    id: number; 
    name: string; 
    industry?: string;
    industry_name?: string;
    size?: string;
    country_code?: string;
    country_name?: string;
    registration_year?: number;
    created_at: string;
  }
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState<'name' | 'created_at' | 'industry' | 'size'>('name');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');

  // Function to format company size
  const formatCompanySize = (size?: string) => {
    if (!size) return '—';
    
    const sizeLower = size.toLowerCase();
    if (sizeLower === 'small') return 'Small';
    if (sizeLower === 'medium') return 'Medium';
    if (sizeLower === 'large') return 'Large';
    if (sizeLower === 'enterprise') return 'Enterprise';
    
    // If it's a number (employee count), categorize it
    const employeeCount = parseInt(size);
    if (!isNaN(employeeCount)) {
      if (employeeCount <= 50) return 'Small';
      if (employeeCount <= 200) return 'Medium';
      if (employeeCount <= 1000) return 'Large';
      return 'Enterprise';
    }
    
    // Default fallback
    return size.charAt(0).toUpperCase() + size.slice(1).toLowerCase();
  };
  
  const { data: companies, isLoading, refetch } = useQuery<Company[]>({
    queryKey: ['/api/users/me/companies'],
    queryFn: getQueryFn(),
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: 'always',
  });

  const filtered = useMemo(() => {
    let list = companies || [];
    
    // Filter by search
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter((c: Company) => 
        c.name.toLowerCase().includes(q) || 
        (c.industry_name || '').toLowerCase().includes(q) ||
        (c.country_code || '').toLowerCase().includes(q)
      );
    }
    
    // Sort
    list.sort((a, b) => {
      let aVal, bVal;
      switch (sortBy) {
        case 'name':
          aVal = a.name.toLowerCase();
          bVal = b.name.toLowerCase();
          break;
        case 'created_at':
          aVal = new Date(a.created_at).getTime();
          bVal = new Date(b.created_at).getTime();
          break;
        case 'industry':
          aVal = (a.industry_name || '').toLowerCase();
          bVal = (b.industry_name || '').toLowerCase();
          break;
        case 'size':
          aVal = formatCompanySize(a.size).toLowerCase();
          bVal = formatCompanySize(b.size).toLowerCase();
          break;
        default:
          return 0;
      }
      
      if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });
    
    return list;
  }, [companies, search, sortBy, sortOrder]);

  const handleSort = (field: 'name' | 'created_at' | 'industry' | 'size') => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 w-full">
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search companies, industry, or country..."
          className="flex-1 border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        {/* Only show Create Company button for system users */}
        {user?.is_system_user && (
          <Link href="/company-profile">
            <Button className="bg-green-600 hover:bg-green-700">
              <i data-feather="plus" className="w-4 h-4 mr-2"></i>
              Create Company
            </Button>
          </Link>
        )}
      </div>
      
      <div className="flex items-center gap-4 text-sm text-gray-600">
        <span>Sort by:</span>
        <button
          onClick={() => handleSort('name')}
          className={`px-2 py-1 rounded ${sortBy === 'name' ? 'bg-blue-100 text-blue-700' : 'hover:bg-gray-100'}`}
        >
          Name {sortBy === 'name' && (sortOrder === 'asc' ? '↑' : '↓')}
        </button>
        <button
          onClick={() => handleSort('industry')}
          className={`px-2 py-1 rounded ${sortBy === 'industry' ? 'bg-blue-100 text-blue-700' : 'hover:bg-gray-100'}`}
        >
          Industry {sortBy === 'industry' && (sortOrder === 'asc' ? '↑' : '↓')}
        </button>
        <button
          onClick={() => handleSort('size')}
          className={`px-2 py-1 rounded ${sortBy === 'size' ? 'bg-blue-100 text-blue-700' : 'hover:bg-gray-100'}`}
        >
          Size {sortBy === 'size' && (sortOrder === 'asc' ? '↑' : '↓')}
        </button>
        <button
          onClick={() => handleSort('created_at')}
          className={`px-2 py-1 rounded ${sortBy === 'created_at' ? 'bg-blue-100 text-blue-700' : 'hover:bg-gray-100'}`}
        >
          Date {sortBy === 'created_at' && (sortOrder === 'asc' ? '↑' : '↓')}
        </button>
      </div>

      {isLoading ? (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Industry</TableHead>
              <TableHead>Size</TableHead>
              <TableHead>Country</TableHead>
              <TableHead>Registered</TableHead>
              <TableHead className="w-48">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {Array.from({ length: 6 }).map((_, i) => (
              <TableRow key={i} className="animate-pulse">
                <TableCell><div className="h-4 w-40 bg-gray-200 rounded" /></TableCell>
                <TableCell><div className="h-4 w-32 bg-gray-200 rounded" /></TableCell>
                <TableCell><div className="h-4 w-24 bg-gray-200 rounded" /></TableCell>
                <TableCell><div className="h-4 w-28 bg-gray-200 rounded" /></TableCell>
                <TableCell><div className="h-4 w-20 bg-gray-200 rounded" /></TableCell>
                <TableCell><div className="h-8 w-28 bg-gray-200 rounded" /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) : filtered && filtered.length > 0 ? (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Industry</TableHead>
              <TableHead>Size</TableHead>
              <TableHead>Country</TableHead>
              <TableHead>Registered</TableHead>
              <TableHead className="w-48">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map((company) => (
              <TableRow key={company.id}>
                <TableCell className="font-medium">{company.name}</TableCell>
                <TableCell className="text-gray-600">{company.industry_name || '—'}</TableCell>
                <TableCell className="text-gray-600">{formatCompanySize(company.size)}</TableCell>
                <TableCell className="text-gray-600">{company.country_name || company.country_code || '—'}</TableCell>
                <TableCell className="text-sm text-gray-500">
                  {company.registration_year || new Date(company.created_at).getFullYear()}
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <Link href={`/company-profile/${company.id}`}>
                      <Button variant="outline" size="sm">
                        <i data-feather="eye" className="w-3 h-3 mr-1"></i>
                        View Details
                      </Button>
                    </Link>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) : (
        <div className="py-8 text-center text-gray-600">
          {search ? 'No companies found matching your search.' : 'No companies found. Create your first company to get started.'}
        </div>
      )}
    </div>
  );
}

export default function Settings() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState('companies');

  // Initialize feather icons
  useEffect(() => {
    if (window.feather) {
      window.feather.replace();
    }
  }, []);

  const { data: companies } = useQuery({
    queryKey: ['/api/users/me/companies'],
    queryFn: getQueryFn(),
  });


  const tabs = [
    { id: 'subscription', label: 'Subscription', icon: 'credit-card' },
    { id: 'companies', label: 'Company Registration', icon: 'layers' },
    { id: 'factors', label: 'Emission Factors', icon: 'sliders' },
    { id: 'targets', label: 'Targets & Alerts', icon: 'target' },
    { id: 'team', label: 'Team Management', icon: 'users' },
    { id: 'integrations', label: 'Integrations', icon: 'link' },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      <div className="flex">
        <Sidebar />
        
        <main className="flex-1 p-8">
          <div className="max-w-7xl mx-auto">
            <div className="mb-8">
              <h1 className="text-3xl font-bold text-gray-900">Settings & Account Management</h1>
              <p className="text-gray-600 mt-2">
                Manage your company profile, emission factors, and account settings
              </p>
            </div>

            {/* Tabs Navigation */}
            <div className="border-b border-gray-200 mb-8">
              <nav className="-mb-px flex space-x-8">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center py-2 px-1 border-b-2 font-medium text-sm ${
                      activeTab === tab.id
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <i data-feather={tab.icon} className="w-4 h-4 mr-2"></i>
                    {tab.label}
                  </button>
                ))}
              </nav>
            </div>

            {/* Tab Content */}
            <div className="space-y-8">
              {/* Subscription Tab */}
              {activeTab === 'subscription' && (
                <SubscriptionManagementPanel />
              )}

              {/* Company Registration Tab */}
              {activeTab === 'companies' && (
                <Card>
                  <CardHeader>
                    <CardTitle>Company Registration</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CompanyRegistrationPanel />
                  </CardContent>
                </Card>
              )}


              {/* Emission Factors Tab */}
              {activeTab === 'factors' && (
                <Card>
                  <CardHeader>
                    <CardTitle>Emission Factors Management</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-6">
                      <div className="bg-blue-50 p-4 rounded-lg">
                        <h3 className="font-medium text-blue-900 mb-2">Custom Emission Factors</h3>
                        <p className="text-sm text-blue-800">
                          Override default emission factors with your organization's specific values. 
                          Custom factors take precedence over standard IPCC/DEFRA values.
                        </p>
                      </div>
                      
                      <div className="border border-dashed border-gray-300 rounded-lg p-8 text-center">
                        <i data-feather="plus-circle" className="w-12 h-12 mx-auto text-gray-400 mb-4"></i>
                        <h3 className="text-lg font-medium text-gray-900 mb-2">Add Custom Emission Factor</h3>
                        <p className="text-gray-600 mb-4">
                          Define custom emission factors for specific activities in your organization.
                        </p>
                        <Button variant="outline">
                          <i data-feather="plus" className="w-4 h-4 mr-2"></i>
                          Add New Factor
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Targets & Alerts Tab */}
              {activeTab === 'targets' && (
                <Card>
                  <CardHeader>
                    <CardTitle>Emission Targets & Alerts</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-6">
                      <div className="bg-green-50 p-4 rounded-lg">
                        <h3 className="font-medium text-green-900 mb-2">Emission Reduction Targets</h3>
                        <p className="text-sm text-green-800">
                          Set science-based targets and track your progress toward carbon neutrality.
                        </p>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <Card>
                          <CardContent className="pt-6">
                            <h4 className="font-medium mb-4">Annual Target Settings</h4>
                            <div className="space-y-4">
                              <Input
                                label="Total Emission Target (kg CO2e)"
                                type="number"
                                placeholder="e.g., 10000"
                              />
                              <Input
                                label="Target Year"
                                type="number"
                                value={new Date().getFullYear() + 1}
                                placeholder="2024"
                              />
                              <Button className="w-full">Set Target</Button>
                            </div>
                          </CardContent>
                        </Card>

                        <Card>
                          <CardContent className="pt-6">
                            <h4 className="font-medium mb-4">Alert Configuration</h4>
                            <div className="space-y-4">
                              <div className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                                <div>
                                  <p className="font-medium">Monthly Threshold Alerts</p>
                                  <p className="text-sm text-gray-500">Notify when emissions exceed monthly targets</p>
                                </div>
                                <button
                                  className="w-6 h-6 bg-blue-600 rounded border-2 border-blue-600"
                                  aria-label="Enable monthly threshold alerts"
                                  title="Enable monthly threshold alerts"
                                ></button>
                              </div>
                              <div className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                                <div>
                                  <p className="font-medium">Data Quality Alerts</p>
                                  <p className="text-sm text-gray-500">Alert when estimated data exceeds 50%</p>
                                </div>
                                <button
                                  className="w-6 h-6 bg-gray-300 rounded border-2 border-gray-300"
                                  aria-label="Enable data quality alerts"
                                  title="Enable data quality alerts"
                                ></button>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Team Management Tab */}
              {activeTab === 'team' && (
                <Card>
                  <CardHeader>
                    <CardTitle>Team Management</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-6">
                      <div className="flex justify-between items-center">
                        <div>
                          <h3 className="font-medium">Team Members</h3>
                          <p className="text-sm text-gray-600">Manage access and roles for your team</p>
                        </div>
                        <Button>
                          <i data-feather="user-plus" className="w-4 h-4 mr-2"></i>
                          Invite Member
                        </Button>
                      </div>

                      <div className="border border-gray-200 rounded-lg overflow-hidden">
                        <div className="bg-gray-50 px-6 py-3 border-b border-gray-200">
                          <div className="grid grid-cols-4 gap-4 text-sm font-medium text-gray-700">
                            <span>Name</span>
                            <span>Email</span>
                            <span>Role</span>
                            <span>Actions</span>
                          </div>
                        </div>
                        <div className="px-6 py-4">
                          <div className="grid grid-cols-4 gap-4 items-center text-sm">
                            <span className="font-medium">{user?.full_name || 'Current User'}</span>
                            <span>{user?.email}</span>
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                              {user?.role || 'Admin'}
                            </span>
                            <span className="text-gray-400">Owner</span>
                          </div>
                        </div>
                      </div>

                      <div className="bg-yellow-50 p-4 rounded-lg">
                        <div className="flex">
                          <i data-feather="info" className="w-5 h-5 text-yellow-600 mr-2"></i>
                          <div>
                            <h4 className="font-medium text-yellow-800">Role Permissions</h4>
                            <div className="text-sm text-yellow-700 mt-1">
                              <p><strong>Admin:</strong> Full access to all features and settings</p>
                              <p><strong>Editor:</strong> Can add and edit emission data</p>
                              <p><strong>Viewer:</strong> Read-only access to reports and data</p>
                              <p><strong>Auditor:</strong> Can review and approve emission entries</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Integrations Tab */}
              {activeTab === 'integrations' && (
                <Card>
                  <CardHeader>
                    <CardTitle>External Integrations</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* ERP Integration */}
                        <div className="border border-gray-200 rounded-lg p-6">
                          <div className="flex items-center mb-4">
                            <i data-feather="database" className="w-8 h-8 text-blue-600 mr-3"></i>
                            <div>
                              <h3 className="font-medium">ERP Systems</h3>
                              <p className="text-sm text-gray-600">Connect to SAP, Oracle, or other ERP systems</p>
                            </div>
                          </div>
                          <Button variant="outline" className="w-full">
                            Configure ERP Integration
                          </Button>
                        </div>

                        {/* Smart Meters */}
                        <div className="border border-gray-200 rounded-lg p-6">
                          <div className="flex items-center mb-4">
                            <i data-feather="zap" className="w-8 h-8 text-green-600 mr-3"></i>
                            <div>
                              <h3 className="font-medium">Smart Meters API</h3>
                              <p className="text-sm text-gray-600">Automated energy consumption data</p>
                            </div>
                          </div>
                          <Button variant="outline" className="w-full">
                            Connect Smart Meters
                          </Button>
                        </div>

                        {/* Cloud Storage */}
                        <div className="border border-gray-200 rounded-lg p-6">
                          <div className="flex items-center mb-4">
                            <i data-feather="cloud" className="w-8 h-8 text-purple-600 mr-3"></i>
                            <div>
                              <h3 className="font-medium">Cloud Storage</h3>
                              <p className="text-sm text-gray-600">AWS, Google Cloud, Azure integration</p>
                            </div>
                          </div>
                          <Button variant="outline" className="w-full">
                            Setup Cloud Sync
                          </Button>
                        </div>

                        {/* IoT Sensors */}
                        <div className="border border-gray-200 rounded-lg p-6">
                          <div className="flex items-center mb-4">
                            <i data-feather="wifi" className="w-8 h-8 text-orange-600 mr-3"></i>
                            <div>
                              <h3 className="font-medium">IoT Sensors</h3>
                              <p className="text-sm text-gray-600">Real-time environmental monitoring</p>
                            </div>
                          </div>
                          <Button variant="outline" className="w-full">
                            Connect IoT Devices
                          </Button>
                        </div>
                      </div>

                      <div className="bg-blue-50 p-4 rounded-lg">
                        <div className="flex">
                          <i data-feather="shield" className="w-5 h-5 text-blue-600 mr-2"></i>
                          <div>
                            <h4 className="font-medium text-blue-900">Security & Privacy</h4>
                            <p className="text-sm text-blue-800 mt-1">
                              All integrations use secure OAuth 2.0 authentication and encrypted data transmission. 
                              Your emission data remains private and under your control.
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}